import { Component, Input, OnInit } from '@angular/core';
import { ResultsFormatEditorService } from '../../../services/results-format-editor.service';
import { publicMethods } from '../../../globals';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'results-format-editor',
  templateUrl: './results-format-editor.component.html',
  styleUrls: ['./results-format-editor.component.css'],
  standalone: false,
})
export class ResultsFormatEditorComponent implements OnInit {
  @Input()
  inputParams: any = {
    isSuperUser: false,
  };

  individualResultsFormatSettings: Array<any>;
  organisationResultsFormatSettings: Array<any>;

  // --- NEW PROPERTIES FOR REFACTORED UI ---
  columnGroups: any[] = [];
  totalColumnCount: number = 0;
  activeTab: 'user' | 'organisation' = 'user';
  columnSearchTerm: string = '';
  // ----------------------------------------

  unusedResultHeaders: any;
  zintHeaderNames: Array<any>;
  customColumns: Array<any>;
  magicInsightsColumns: Array<any>;

  resultsFormatBeingEdited: any;
  nameBeingChanged = '';
  newResultsFormatName = '';

  constructor(
    private resultsFormatEditorService: ResultsFormatEditorService,
    private pubMethods: publicMethods
  ) {}

  isLoadingSignal = this.resultsFormatEditorService.isLoadingSignal;

  ngOnInit(): void {
    this.getInitialUserResultsFormatData();
  }

  getInitialUserResultsFormatData() {
    this.resultsFormatEditorService
      .getUserResultsFormat()
      .subscribe(data => this.updateUIWithInitialUserResultsFormatData(data));
  }

  updateUIWithInitialUserResultsFormatData(data) {
    this.zintHeaderNames = data.results_header_names;
    this.customColumns = data.custom_columns;
    this.magicInsightsColumns = data.magic_insights_header_options;

    this.individualResultsFormatSettings = data.individual;
    this.organisationResultsFormatSettings = data.organisation;

    this.columnGroups = [
      {
        name: 'Core Zint Data',
        items: this.zintHeaderNames,
        open: true,
      },
      {
        name: 'Custom Columns',
        items: this.customColumns,
        open: false,
      },
      {
        name: 'Magic Insights',
        items: this.magicInsightsColumns,
        open: false,
      },
    ];

    this.totalColumnCount =
      this.zintHeaderNames.length +
      this.customColumns.length +
      this.magicInsightsColumns.length;

    this.activateUserSettings();
  }

  activateUserSettings() {
    this.activeTab = 'user';
    this.columnSearchTerm = '';
    if (this.individualResultsFormatSettings.length > 0) {
      this.resultsFormatBeingEdited = this.individualResultsFormatSettings[0];
      this.userResultsFormatEditingChanged();
    } else {
      this.resultsFormatBeingEdited = null;
    }
  }

  activateOrganisationSettings() {
    this.activeTab = 'organisation';
    this.columnSearchTerm = '';
    if (this.organisationResultsFormatSettings.length > 0) {
      this.resultsFormatBeingEdited = this.organisationResultsFormatSettings[0];
      this.userResultsFormatEditingChanged();
    } else {
      this.resultsFormatBeingEdited = null;
    }
  }

  saveResultsFormatName(resultsFormatSettings) {
    this.resultsFormatEditorService
      .updateResultsFormatName(
        'user',
        resultsFormatSettings.id,
        this.nameBeingChanged
      )
      .subscribe(data =>
        this.validateSuccessfulNameChange(resultsFormatSettings, data)
      );
  }

  saveOrganisationResultsFormatName(resultsFormatSettings) {
    this.resultsFormatEditorService
      .updateResultsFormatName(
        'organisation',
        resultsFormatSettings.id,
        this.nameBeingChanged
      )
      .subscribe(data =>
        this.validateSuccessfulNameChange(resultsFormatSettings, data)
      );
  }

  validateSuccessfulNameChange(resultsFormatSettings, data) {
    if (data.success) {
      resultsFormatSettings.name = this.nameBeingChanged;
      this.pubMethods.showInfoMessage('Your settings have been updated.');
    }
  }

  validateSuccessfulSettingsChange(data) {
    if (data.success) {
      this.pubMethods.showInfoMessage('Your settings have been updated.');
    }
  }


  userResultsFormatEditingChanged() {
    if (this.resultsFormatBeingEdited) {
      this.parseResultsFormat();
      // Set the name for the "Edit Name" box
      this.nameBeingChanged = this.resultsFormatBeingEdited.name;
    }
  }

  parseResultsFormat() {
    if (
      this.resultsFormatBeingEdited?.format &&
      !this.resultsFormatBeingEdited.format.every(el => typeof el === 'object')
    ) {
      const parsedFormat = [];
      this.resultsFormatBeingEdited.format.forEach(el => {
        try {
          parsedFormat.push(JSON.parse(el));
        } catch (err) {
          parsedFormat.push(el);
        }
      });
      this.resultsFormatBeingEdited.format = parsedFormat;
    }
  }

  createNewResultsFormat() {
    this.resultsFormatEditorService
      .createNewResultsFormat('user', this.newResultsFormatName, [
        'Company Name',
      ])
      .subscribe(data => this.postCreateAddNewIndividualResultsFormat(data));
  }

  createNewOrganisationResultsFormat() {
    this.resultsFormatEditorService
      .createNewResultsFormat('organisation', this.newResultsFormatName, [
        'Company Name',
      ])
      .subscribe(data => this.postCreateAddNewOrganisationResultsFormat(data));
  }

  postCreateAddNewIndividualResultsFormat(data) {
    this.individualResultsFormatSettings.push(data);
    this.resultsFormatBeingEdited =
      this.individualResultsFormatSettings[
        this.individualResultsFormatSettings.length - 1
      ];
    this.userResultsFormatEditingChanged();
  }

  postCreateAddNewOrganisationResultsFormat(data) {
    this.organisationResultsFormatSettings.push(data);
    this.resultsFormatBeingEdited =
      this.organisationResultsFormatSettings[
        this.organisationResultsFormatSettings.length - 1
      ];
    this.userResultsFormatEditingChanged();
  }

  saveResultsFormat(): void {
    this.resultsFormatEditorService
      .saveResultsFormat(
        'user',
        this.resultsFormatBeingEdited.id,
        this.resultsFormatBeingEdited.format,
        this.resultsFormatBeingEdited.name // Use the name from the object
      )
      .subscribe(data =>
        this.processIndividualResultsFormatColumnsChanged(
          this.resultsFormatBeingEdited.id,
          data
        )
      );
  }
  
  saveOrganisationResultsFormat(): void {
    this.resultsFormatEditorService
      .saveResultsFormat(
        'organisation',
        this.resultsFormatBeingEdited.id,
        this.resultsFormatBeingEdited.format,
        this.resultsFormatBeingEdited.name // Use the name from the object
      )
      .subscribe(data =>
        this.processOrganisationResultsFormatColumnsChanged(
          this.resultsFormatBeingEdited.id,
          data
        )
      );
  }

  dragSort(event: CdkDragDrop<any>): void {
    moveItemInArray(
      this.resultsFormatBeingEdited.format,
      event.previousIndex,
      event.currentIndex
    );
  }
  
  processIndividualResultsFormatColumnsChanged(idOfChangedFormat, data): void {
    if (data.id) {
      this.pubMethods.showInfoMessage('Your settings have been updated.');
      // Update the object in the list
      const index = this.individualResultsFormatSettings.findIndex(
        format => format.id === idOfChangedFormat
      );
      if (index !== -1) {
        this.individualResultsFormatSettings[index] = data;
      }
      // Ensure the edited object is the updated one
      this.resultsFormatBeingEdited = data;
      this.userResultsFormatEditingChanged();
    }
  }
  
  processOrganisationResultsFormatColumnsChanged(
    idOfChangedFormat,
    data
  ): void {
    if (data.id) {
      this.pubMethods.showInfoMessage('Your settings have been updated.');
      // Update the object in the list
      const index = this.organisationResultsFormatSettings.findIndex(
        format => format.id === idOfChangedFormat
      );
      if (index !== -1) {
        this.organisationResultsFormatSettings[index] = data;
      }
      // Ensure the edited object is the updated one
      this.resultsFormatBeingEdited = data;
      this.userResultsFormatEditingChanged();
    }
  }

  changeDefaultFormat(formatId): void {
    if (formatId) {
      this.resultsFormatEditorService
        .selectDefaultResultsFormat('user', formatId)
        .subscribe(data => this.validateSuccessfulSettingsChange(data));
    }
  }

  changeDefaultOrganisationFormat(formatId): void {
    if (formatId){
      this.resultsFormatEditorService
      .selectDefaultResultsFormat('organisation', formatId)
      .subscribe(data => this.validateSuccessfulSettingsChange(data));
    }
  }

  deleteFormat(): void {
    if (this.resultsFormatBeingEdited) {
      this.resultsFormatEditorService
        .deleteResultsFormat('user', this.resultsFormatBeingEdited.id)
        .subscribe(data =>
          this.processFormatDeletedResponse(
            data,
            this.resultsFormatBeingEdited.id
          )
        );
    }
  }

  deleteOrganisationFormat(): void {
    if (this.resultsFormatBeingEdited) {
      this.resultsFormatEditorService
        .deleteResultsFormat('organisation', this.resultsFormatBeingEdited.id)
        .subscribe(data =>
          this.processFormatDeletedResponse(
            data,
            this.resultsFormatBeingEdited.id
          )
        );
    }
  }

  processFormatDeletedResponse(data, idDeleted): void {
    if (data.success) {
      this.pubMethods.showInfoMessage('Deleted Successfully');
      this.individualResultsFormatSettings =
        this.individualResultsFormatSettings.filter(
          format => format.id !== idDeleted
        );
      this.organisationResultsFormatSettings =
        this.organisationResultsFormatSettings.filter(
          format => format.id !== idDeleted
        );

      // Reselect the first available view
      if (this.activeTab === 'user') {
        this.activateUserSettings();
      } else {
        this.activateOrganisationSettings();
      }
    }
  }

  // -----------------------------------------------------------------
  // --- NEW HELPER METHODS FOR REFACTORED UI ---
  // -----------------------------------------------------------------

  private getHeaderName(header: any): string {
    if (typeof header === 'string') {
      return header;
    }
    if (header && header.name) {
      return header.name;
    }
    return '';
  }

  getFilteredList(list: any[], searchTerm: string): any[] {
    if (!list) {
      return [];
    }
    if (!searchTerm) {
      return list;
    }
    const term = searchTerm.toLowerCase();
    return list.filter(header =>
      this.getHeaderName(header).toLowerCase().includes(term)
    );
  }

  getHeaderId(header: any, groupName: string): string {
    const name = this.getHeaderName(header).replace(/\s/g, '');
    const id = header.id || '';
    return `${groupName}-${name}-${id}`;
  }

  headersAreEqual(h1: any, h2: any): boolean {
    if (typeof h1 !== typeof h2) return false;

    if (typeof h1 === 'string') {
      return h1 === h2;
    }

    if (h1.is_magic_insights_column || h2.is_magic_insights_column) {
      return (
        h1.is_magic_insights_column === true &&
        h2.is_magic_insights_column === true &&
        h1.id === h2.id &&
        h1.key === h2.key
      );
    }

    if (h1.id && h2.id) {
      const h1Propensity = !!h1.is_propensity_model_components_column;
      const h2Propensity = !!h2.is_propensity_model_components_column;
      return h1.id === h2.id && h1Propensity === h2Propensity;
    }

    return false;
  }

  isHeaderSelected(header: any): boolean {
    if (!this.resultsFormatBeingEdited?.format) {
      return false;
    }
    return this.resultsFormatBeingEdited.format.some(h =>
      this.headersAreEqual(h, header)
    );
  }

  onCheckboxChange(event: any, header: any) {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      this.resultsFormatBeingEdited.format.unshift(header);
    } else {
      this.removeHeaderFromSelection(header);
    }
  }

  removeHeaderFromSelection(headerToRemove: any) {
    if (!this.resultsFormatBeingEdited?.format) {
      return;
    }
    this.resultsFormatBeingEdited.format =
      this.resultsFormatBeingEdited.format.filter(
        h => !this.headersAreEqual(h, headerToRemove)
      );
  }

  getSelectedCountForGroup(group: any): number {
    if (!this.resultsFormatBeingEdited?.format) {
      return 0;
    }
    return group.items.filter(item => this.isHeaderSelected(item)).length;
  }

  isGroupAllSelected(group: any): boolean {
    if (!this.resultsFormatBeingEdited?.format) {
      return false;
    }
    const listToCompare = this.getFilteredList(
      group.items,
      this.columnSearchTerm
    );
    if (listToCompare.length === 0) return false;

    return listToCompare.every(item => this.isHeaderSelected(item));
  }

  toggleSelectSection(event: any, group: any) {
    const isChecked = (event.target as HTMLInputElement).checked;
    const itemsToToggle = this.getFilteredList(
      group.items,
      this.columnSearchTerm
    );

    itemsToToggle.forEach(item => {
      const isAlreadySelected = this.isHeaderSelected(item);
      if (isChecked && !isAlreadySelected) {
        this.resultsFormatBeingEdited.format.unshift(item);
      } else if (!isChecked && isAlreadySelected) {
        this.removeHeaderFromSelection(item);
      }
    });
  }

  saveChanges() {
    if (this.activeTab === 'user') {
      this.saveResultsFormat();
    } else {
      this.saveOrganisationResultsFormat();
    }
  }
}
